import {
  APP_DATE_FORMAT_EXPANDED,
  APP_DATE_FORMAT_SHORT,
  AUTHORITIES
} from "@/config/constants";
import { useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoCourseComment } from "@/shared/model/edo-course-comment.model";
import { IEdoCoursePost } from "@/shared/model/edo-course-post.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { IEdoWallComment } from "@/shared/model/edo-wall-comment.model";
import { IEdoWallPost } from "@/shared/model/edo-wall-post.model";
import { IUser } from "@/shared/model/user.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import {
  Avatar,
  Badge,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
} from "@/components/ui";
import { Download, MoreVertical } from "lucide-react";
import React, { useEffect, useState } from "react";
import { TextFormat } from "react-jhipster";
import Linkify from "react-linkify";
import CreateComment from "./components/CreateComment";

type PostProps = {
  post: IEdoWallPost | IEdoCoursePost;
  getCommentList: () => Promise<IEdoWallComment[]>;
  saveComment: (comment: IEdoWallComment) => Promise<IEdoWallComment>;
  defaultValueComment: IEdoWallComment;
  onEdit: (post: IEdoWallPost) => void;
  onDelete: (post: IEdoWallPost) => void;
} | {
  post: IEdoCoursePost;
  getCommentList: () => Promise<IEdoCourseComment[]>;
  saveComment: (comment: IEdoCourseComment) => Promise<IEdoCourseComment>;
  defaultValueComment: IEdoCourseComment;
  onEdit: (post: IEdoCoursePost) => void;
  onDelete: (post: IEdoCoursePost) => void;
};

const Post = (props: PostProps) => {
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const [commentList, setCommentList] = useState<IEdoWallComment[] | IEdoCourseComment[]>(
    []
  );
  const [localPost, setLocalPost] = useState<IEdoWallPost | IEdoCoursePost>({ ...props.post });

  const {
    post,
    getCommentList,
    saveComment,
    defaultValueComment,
    onEdit,
    onDelete,
  } = props;

  const openFile = async (file: IEdoFile) => {
    // await axios.get(`/files/${file.id}/view`, { responseType: 'blob'});
    window.open(`/api/files/${file.id}/view`, "_blank");
  };

  const downloadFile = (file: IEdoFile) => {
    // const response = await axios.get(`/files/${file.id}/download`);
    window.open(`/api/files/${file.id}/download`, "_blank");
  };

  const getCommentListLocal = async () => {
    const data = await getCommentList();
    setCommentList(data);
  };

  const openMenu = (_event: React.MouseEvent<HTMLButtonElement>) => {};

  const closeMenu = () => {};

  const handleEdit = () => {
    onEdit(localPost);
  };

  const handleDelete = () => {
    onDelete(localPost);
  };

  useEffect(() => {
    if (post) {
      setLocalPost(post);
    }
  }, [post]);

  useEffect(() => {
    if (localPost) {
      getCommentListLocal();
    }
  }, [localPost]);

  const getAuthorRoleChip = (author: IUser) => {
    let label = "Utente";

    if (hasAnyAuthority(author.roles, [AUTHORITIES.ROLE_ADMIN])) {
      label = "Amministratore";
    } else if (hasAnyAuthority(author.roles, [AUTHORITIES.ROLE_VICE])) {
      label = "Vicepreside";
    } else if (hasAnyAuthority(author.roles, [AUTHORITIES.ROLE_TEACHER, AUTHORITIES.ROLE_SUPPORT_TEACHER])) {
      label = "Docente";
    } else if (hasAnyAuthority(author.roles, [AUTHORITIES.ROLE_STUDENT])) {
      label = "Studente";
    } else if (hasAnyAuthority(author.roles, [AUTHORITIES.ROLE_PARENT])) {
      label = "Genitore";
    } else {
      return null;
    }

    return (
      <Badge variant="secondary" className="text-xs">
        {label}
      </Badge>
    );
  };

  return (
    <Card className="flex-1 overflow-hidden rounded-3xl border border-[var(--color-mockup-border)] shadow-card p-0">
      <div
        className="h-1.5"
        style={{ background: 'linear-gradient(90deg, var(--color-primary), var(--color-mockup-sky))' }}
      />
      {!localPost ? (
        <div className="flex items-center justify-center p-6">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <div className="px-6 pb-5 pt-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Avatar
                  className="h-10 w-10 shrink-0"
                  fallback={`${localPost.author.firstName?.[0] ?? ""}${localPost.author.lastName?.[0] ?? ""}`}
                />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm font-heading text-[var(--color-mockup-text)]">
                      {localPost.author.firstName + " " + localPost.author.lastName}
                    </span>
                    {getAuthorRoleChip(localPost.author)}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {localPost.createdAt ? (
                      <span className="text-xs text-[var(--color-mockup-text-light)]">
                        <TextFormat
                          type="date"
                          value={localPost.createdAt}
                          format={APP_DATE_FORMAT_EXPANDED}
                          locale="it"
                        />
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              {post.author.id === account.id ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton aria-label="Menu" className="h-9 w-9 rounded-full">
                      <MoreVertical className="h-5 w-5" />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleEdit}>Modifica</DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete}>Elimina</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>
            <p className="whitespace-pre-line text-sm font-editorial text-[var(--color-mockup-text-mid)] leading-relaxed mb-5">
              <Linkify
                componentDecorator={(decoratorHref, decoratorText, key) => (
                  <a
                    key={key}
                    href={decoratorHref}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    {decoratorText}
                  </a>
                )}
              >
                {localPost.description}
              </Linkify>
            </p>
          </div>
          {localPost.files.length ? (
            <div className="border-t border-[var(--color-mockup-border)]">
              <List>
                {localPost.files.map((file) => (
                  <ListItem
                    key={file.id}
                    className="list-none cursor-pointer px-4 py-3 transition-colors hover:bg-primary/5"
                    onClick={() => openFile(file)}
                  >
                    <ListItemIcon>
                      {getFileAvatar(file.fileType)}
                    </ListItemIcon>
                    <ListItemText
                      primary={<span className="min-w-0 truncate">{file.originalFileName}</span>}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={(e) => { e.stopPropagation(); downloadFile(file); }}
                        aria-label="Download file"
                        className="rounded-full"
                      >
                        <Download className="h-5 w-5" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </div>
          ) : null}
          {commentList !== null && commentList.length ? (
            <div className="border-t border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)] px-6 pb-6 pt-2">
              {commentList.map((comment, i) => (
                <div
                  key={comment.id}
                  className={`flex gap-3 mt-3 ${i === commentList?.length - 1 ? "" : "mb-3"}`}
                >
                  <Avatar
                    className="h-8 w-8 shrink-0"
                    fallback={`${(comment as IEdoWallComment).author?.firstName?.[0] ?? ""}${(comment as IEdoWallComment).author?.lastName?.[0] ?? ""}`}
                  />
                  <div className="min-w-0 flex-1 rounded-2xl border border-[var(--color-mockup-border)] bg-surface px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold font-heading text-[var(--color-mockup-text)]">
                        {(comment as IEdoWallComment).author?.firstName + " " + (comment as IEdoWallComment).author?.lastName}
                      </span>
                      <span className="text-xs text-[var(--color-mockup-text-light)]">
                        <TextFormat
                          type="date"
                          value={comment.createdAt}
                          format={APP_DATE_FORMAT_SHORT}
                          locale="it"
                        />
                      </span>
                    </div>
                    <p className="whitespace-pre-line text-xs text-[var(--color-mockup-text-mid)]">
                      {comment.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <div className="border-t border-[var(--color-mockup-border)] px-6 py-4">
            <CreateComment
              reloadCommentList={getCommentListLocal}
              saveComment={saveComment}
              defaultValueComment={defaultValueComment}
            />
          </div>
        </>
      )}
    </Card>
  );
};

export default Post;